import { Close16Icon } from '@jablotron/lib.spa-icons/icons/close-16';
import { Search16Icon } from '@jablotron/lib.spa-icons/icons/search-16';
import { useLanguage, useTranslate } from '@jablotron/lib.spa-lang';
import { Link, useLocation, useNavigate, useSearch } from '@tanstack/react-router';
import { type ChangeEvent, type KeyboardEvent, useEffect, useMemo, useRef, useState } from 'react';
import { FallbackImage } from '~/components/image/FallbackImage';
import { Overline } from '~/components/overline/Overline';
import { SearchInput } from '~/components/search-input/SearchInput';
import { getCatalog } from '~/entities/catalog/api';
import { type CatalogSearchResult, searchCatalog } from '~/entities/catalog/search';
import type { Catalog, LocaleCode } from '~/entities/catalog/types';
import { DiscontinuedBadge } from '~/entities/catalog/ui/DiscontinuedBadge';
import { ProductCode } from '~/entities/catalog/ui/ProductCode';
import { Texts } from '~/texts/runtime';
import { productPhotoUrl } from '~/entities/catalog/content-cdn';

const MAX_PRODUCTS = 6;
const MAX_CATEGORIES = 4;

export function CatalogSearchBox({
  className = 'max-w-[420px]',
  variant = 'header',
  placeholder = Texts.SearchPlaceholder,
  autoFocus = false,
}: {
  className?: string;
  variant?: 'header' | 'hero';
  /** Translation key for the placeholder (and accessible name); defaults to the header's generic hint. */
  placeholder?: string;
  autoFocus?: boolean;
}) {
  const t = useTranslate();
  const navigate = useNavigate();
  const { search: searchParam } = useSearch({ from: '__root__' });
  // Catalog-wide search: the query lives in the URL only on its own results page. Elsewhere typing
  // stays local, so it never rewrites the current page's URL (which that page would ignore anyway).
  const isSearchPage = useLocation({
    select: (location) => location.pathname === '/search',
  });
  const searchFromUrl = isSearchPage ? searchParam : undefined;
  const [currentLanguage] = useLanguage();
  const locale = (currentLanguage === 'cs' ? 'cs' : 'en') as LocaleCode;
  const [catalog, setCatalog] = useState<Catalog | null>(null);
  const [query, setQuery] = useState(searchFromUrl ?? '');
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    void getCatalog().then(setCatalog);
  }, []);

  useEffect(() => {
    setQuery(searchFromUrl ?? '');
  }, [searchFromUrl]);

  useEffect(() => {
    const onPointerDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onPointerDown);

    return () => document.removeEventListener('mousedown', onPointerDown);
  }, []);

  const goToSearchPage = (value: string) => {
    const trimmed = value.trim();
    if (!trimmed) {
      return;
    }
    setOpen(false);
    void navigate({
      to: '/search',
      search: { search: trimmed },
    });
  };

  const result: CatalogSearchResult | null = useMemo(() => {
    if (!catalog || !query.trim()) {
      return null;
    }

    return searchCatalog(catalog, query, locale, t);
  }, [catalog, query, locale, t]);

  const totalProducts = result?.products.length ?? 0;
  const products = result?.products.slice(0, MAX_PRODUCTS) ?? [];
  const categories = result?.categories.slice(0, MAX_CATEGORIES) ?? [];
  const hasHits = products.length > 0 || categories.length > 0;
  const showDropdown = open && query.trim().length > 0;
  const showAllLink = totalProducts > 0;
  const isHero = variant === 'hero';

  const inputProps = {
    value: query,
    onChange: (e: ChangeEvent<HTMLInputElement>) => {
      setQuery(e.target.value);
      setOpen(true);
    },
    onFocus: () => setOpen(true),
    onKeyDown: (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Escape') {
        setOpen(false);
      }
      if (e.key === 'Enter') {
        e.preventDefault();
        goToSearchPage(query);
      }
    },
    placeholder: t(placeholder),
    autoFocus,
    'aria-label': t(placeholder),
    title: t(Texts.SearchCodeHint),
    autoComplete: 'off',
  };

  const clearButton = query ? (
    <button
      type="button"
      onClick={() => {
        setQuery('');
        setOpen(false);
      }}
      className="absolute right-2 top-1/2 flex size-6 -translate-y-1/2 cursor-pointer items-center justify-center rounded border-none bg-transparent p-0 text-brand-subtle hover:text-brand-ink"
      aria-label={t(Texts.SearchClear)}
      title={t(Texts.SearchClear)}
    >
      <Close16Icon width={14} height={14} aria-hidden />
    </button>
  ) : undefined;

  return (
    <div ref={rootRef} className={`relative w-full ${className}`}>
      <div className={isHero ? 'flex items-stretch gap-2' : ''}>
        {isHero ? (
          <div className="relative min-w-0 flex-1">
            <Search16Icon
              width={14}
              height={14}
              aria-hidden
              className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-brand-subtle"
            />
            <input
              {...inputProps}
              className={`w-full rounded-catalog-lg border-2 border-brand-cyan bg-white py-3.5 pl-11 text-base text-brand-ink outline-none transition-shadow focus:shadow-[0_0_0_2px_#0085cf] ${
                query ? 'pr-10' : 'pr-4'
              }`}
            />
            {clearButton}
          </div>
        ) : (
          <SearchInput {...inputProps} className="min-w-0 flex-1" trailing={clearButton} />
        )}

        {isHero && (
          <button
            type="button"
            onClick={() => goToSearchPage(query)}
            className="shrink-0 rounded-catalog-lg bg-brand-yellow px-6 py-3.5 text-base font-semibold text-brand-ink outline-none transition-colors hover:bg-brand-yellowDark focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-cyanDark"
          >
            {t(Texts.SearchSubmit)}
          </button>
        )}
      </div>

      {showDropdown && (
        <div className="absolute inset-x-0 top-[calc(100%+6px)] z-[60] max-h-[420px] overflow-y-auto rounded-xl border border-primary bg-white shadow-lg">
          {!hasHits && <p className="m-0 px-4 py-3 text-sm text-brand-subtle">{t(Texts.SearchNoResults)}</p>}

          {categories.length > 0 && (
            <div className="border-b border-primary p-2">
              <Overline className="mb-1 px-2">{t(Texts.SearchCategories)}</Overline>
              {categories.map((cat) => (
                <Link
                  key={cat.id}
                  to="/categories/$categoryId"
                  params={{ categoryId: cat.slug }}
                  onClick={() => setOpen(false)}
                  className="flex items-center gap-2 rounded-lg p-2 text-sm text-brand-ink no-underline hover:bg-brand-surface"
                >
                  <span className="font-normal">{t(cat.title)}</span>
                </Link>
              ))}
            </div>
          )}

          {products.length > 0 && (
            <div className="p-2">
              <Overline className="mb-1 px-2">{t(Texts.SearchProducts)}</Overline>
              {products.map((product) => (
                <Link
                  key={product.id}
                  to="/products/$id"
                  params={{ id: product.id }}
                  onClick={() => setOpen(false)}
                  className="flex items-center gap-3 rounded-lg p-2 text-inherit no-underline hover:bg-brand-surface"
                >
                  <div className="flex size-10 shrink-0 items-center justify-center rounded-md bg-brand-surface">
                    <FallbackImage
                      src={productPhotoUrl(product.image, 'small')}
                      alt=""
                      className="size-8 object-contain"
                    />
                  </div>
                  <div className="min-w-0">
                    <p className="m-0 truncate text-sm font-normal text-brand-ink">{t(product.name)}</p>
                    <p className="m-0 text-xs text-brand-subtle">
                      <ProductCode code={product.code} />
                      <DiscontinuedBadge product={product} className="ml-2" />
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          )}

          {showAllLink && (
            <div className="border-t border-primary p-2">
              <button
                type="button"
                onClick={() => goToSearchPage(query)}
                className="w-full cursor-pointer rounded-lg border-none bg-transparent p-2 text-left text-sm font-semibold text-brand-cyanText hover:bg-brand-surface"
              >
                {t(Texts.SearchShowAll)}
                {totalProducts > MAX_PRODUCTS ? ` (${totalProducts})` : ''}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
